import { Icon, styled, Tooltip } from 'opensolar-ui'
import React from 'react'

const AttachmentBadgeContainer = styled('div')({
  position: 'absolute',
  top: -6,
  right: -6,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  zIndex: 100,
})

const AttachmentIconWrapper = styled('span')({
  position: 'relative',
  marginRight: -8,
  '&:not(:last-child)': {
    marginRight: -8,
  },
})

const AttachmentIcon = styled('span')<{ isImage?: boolean; isPdf?: boolean }>(({ isImage, isPdf }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: '#ffffff',
  border: '1.5px solid #e0e0e0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  color: '#666',
  fontSize: 12,
  overflow: 'hidden',
  ...(isImage && {
    backgroundColor: '#e3f2fd',
    borderColor: '#2196f3',
    color: '#1976d2',
  }),
  ...(isPdf && {
    backgroundColor: '#ffebee',
    borderColor: '#f44336',
    color: '#d32f2f',
  }),
}))

const AttachmentImageThumbnail = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

const AttachmentCount = styled('div')({
  fontSize: 9,
  fontWeight: 600,
  color: '#666',
  backgroundColor: '#ffffff',
  borderRadius: '50%',
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1.5px solid #e0e0e0',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
})

const TooltipContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxWidth: '280px',
})

const TooltipFileName = styled('div')({
  fontWeight: 600,
  fontSize: '13px',
  color: '#111827',
  wordBreak: 'break-word',
})

interface AttachmentBadgeProps {
  fileAttachments: Array<{
    file_id?: string
    file_name: string
    file_type: string
    previewUrl?: string
  }>
}

const getAttachmentContent = (file: { file_type: string; file_name: string; previewUrl?: string }) => {
  const isImage = file.file_type === 'image'
  const isPdf = file.file_name.toLowerCase().endsWith('.pdf')

  if (isImage && file.previewUrl) {
    return <AttachmentImageThumbnail src={file.previewUrl} alt={file.file_name} />
  } else if (isImage) {
    return <Icon icon="image" size="small" />
  } else if (isPdf) {
    return <Icon icon="picture_as_pdf" size="small" />
  } else {
    return <Icon icon="insert_drive_file" size="small" />
  }
}

export const AttachmentBadge: React.FC<AttachmentBadgeProps> = ({ fileAttachments }) => {
  if (fileAttachments.length === 0) {
    return null
  }

  const visibleAttachments = fileAttachments.slice(0, 2)
  const remainingCount = Math.max(0, fileAttachments.length - 2)

  return (
    <Tooltip
      title={
        <TooltipContent>
          {fileAttachments.map((file, index) => (
            <TooltipFileName key={index}>{file.file_name}</TooltipFileName>
          ))}
        </TooltipContent>
      }
      arrow
    >
      <AttachmentBadgeContainer>
        {remainingCount > 0 && (
          <AttachmentIconWrapper style={{ zIndex: visibleAttachments.length + 1 }}>
            <AttachmentCount>+{remainingCount}</AttachmentCount>
          </AttachmentIconWrapper>
        )}
        {visibleAttachments.map((file, index) => {
          const isImage = file.file_type === 'image'
          const isPdf = file.file_name.toLowerCase().endsWith('.pdf')
          const content = getAttachmentContent(file)

          return (
            <AttachmentIconWrapper key={index} style={{ zIndex: visibleAttachments.length - index }}>
              <AttachmentIcon isImage={isImage} isPdf={isPdf}>
                {content}
              </AttachmentIcon>
            </AttachmentIconWrapper>
          )
        })}
      </AttachmentBadgeContainer>
    </Tooltip>
  )
}
